import { useSharedContext } from '../../context/SharedContext';
import { useEffect, useState } from 'react';
import { OrderDto } from '../../api/dto/OrderDto';
import { AuthenticatedUser } from '../../security/auth/AuthenticatedUser';
import { Modal } from 'react-responsive-modal';
import { Alerter } from '../../layout/Alerter';
import { ShopifySupportApi } from '../../api/ShopifySupportApi';
import { QueryClient, useQuery, useQueryClient } from '@tanstack/react-query';
import "./Orders.css";
import "./Shopify.css";
import TextWithNewlines from './TextWithNewlines';

class DaysSelection {
    key: string;
    value: string;
    title: string;

    constructor(value: string, title: string) {
        this.key = value;
        this.value = value;
        this.title = title;
    }
}

export default function Orders() {

    const sharedContextData = useSharedContext();
    const queryClient: QueryClient = useQueryClient();

    //const [orderItems, setOrderItems] = useState<OrderDto[] | null>(null);

    const [detailItem, setDetailItem] = useState<OrderDto | null>(null);
    const [detailsOpen, setDetailsOpen] = useState<boolean>(false);

    const [mmcOrderOpen, setMmcOrderOpen] = useState<boolean>(false);
    const [mmcOrder, setMmcOrder] = useState<string>("");

    const [lookback, setLookback] = useState<number>(1);

    // const loadOrderItems = async (user: AuthenticatedUser) => {
    //     const lookup = await ShopifySupportApi.getOrders(MaxDays, user);
    //     if (lookup) {
    //         setOrderItems(lookup);
    //     } else {
    //         Alerter.showError('Error encountered while loading order items', Alerter.DEFAULT_TIMEOUT);
    //         setOrderItems([]);
    //     }
    // }

    function getLookbackOptions(): DaysSelection[] {
        const options: DaysSelection[] = [];
        options.push(new DaysSelection("1", "Within a day"));
        options.push(new DaysSelection("3", "Within 3 days"));
        options.push(new DaysSelection("7", "Within a week"));
        options.push(new DaysSelection("14", "Within 2 weeks"));
        options.push(new DaysSelection("21", "Within 3 weeks"));
        options.push(new DaysSelection("28", "Within 4 weeks"));
        options.push(new DaysSelection("9", "Within 6 weeks"));
        return options;
    }

    function changeLookup(value: string) {
        setLookback(parseInt(value, 10));
    }

    function openDetailsFor(item: OrderDto) {
        setDetailItem(item);
        setDetailsOpen(true);
    }

    function closeDetailsModal() {
        setDetailsOpen(false);
    }

    // function formatMmcOrder(value: string): string {
    //     let parts = value.split('\n');
    //     let text = "";
    //     parts.forEach(part => {
    //        text += part;
    //        text += "<br/>";
    //     });
    //     return text;
    // }

    async function openMmcOrderFor(item: OrderDto) {
        const result = await ShopifySupportApi.getMmcOrder(item.orderId, authenticatedUser!);
        if (!result)
        {
            return;
        }
        setMmcOrder(result);
        setMmcOrderOpen(true);
    }

    function closeMmcOrderModal() {
        setMmcOrderOpen(false);
    }

    const getAuthenticatedUser = (): Promise<AuthenticatedUser> => {
        return new Promise<AuthenticatedUser>((resolve, reject) => {
            const result = sharedContextData.getAuthenticatedUser();
            if (result == null) {
                reject("Unable to find authenticated user");
            }
            resolve(result!);
        });
    }

    const {data: authenticatedUser, isLoading: isLoadingAuthenticatedUser } = useQuery({
        queryKey: ["authenticatedUser"],
        queryFn: getAuthenticatedUser,
        enabled: true,
        retry: true,
        retryDelay: 500,
        //refetchInterval: (data) => (data === null ? 5000 : false),
    });

    const loadOrders = async (days: number, user: AuthenticatedUser): Promise<OrderDto[]> => {
        return new Promise<OrderDto[]>(async (resolve, reject) => {
            const result = await ShopifySupportApi.getOrders(days, user);
            if (result == null) {
                reject("Unable to find orders");
            } else {
                // @ts-ignore
                resolve(result);
            }
        });
    }

    const {data: orders, isLoading: isLoadingOrders } = useQuery({
        queryKey: ["orders", lookback],
        queryFn: () => loadOrders(lookback, authenticatedUser!),
        retry: 3,
        retryDelay: 500,
        enabled: !!authenticatedUser
    });

    //
    // useEffect(() => {
    //     const user = sharedContextData.getAuthenticatedUser();
    //     if (user != null) {
    //         loadOrderItems(user)
    //             .catch(() => {
    //                 console.log("Error loading order items")
    //             });
    //     }
    // }, []);

    if (isLoadingAuthenticatedUser) {
        return (
            <div>
                Loading authenticated user....
                <div className="ca-spinner-box spinner-border text-primary"><span className="sr-only"></span></div>
            </div>
        );
    }

    if (isLoadingOrders) {
        return (
            <div>
                Loading orders for date range....
                <div className="ca-spinner-box spinner-border text-primary"><span className="sr-only"></span></div>
            </div>
        );
    }

    return (
        <div>
            <h2><span className="ca-blue">Orders for the Past {lookback == 1 ? "" : lookback} Day{lookback == 1 ? "" : "s"} </span></h2>
            <div className="row">
                &nbsp;
            </div>
            <div className="display-choice-area">
                <div className="display-choice-block">
                    <span><h5>Date range:</h5></span>
                    <select value={lookback.toString(10)} onChange={e => changeLookup(e.target.value)}>
                        {getLookbackOptions().map((choice, index) => (
                            <option key={choice.key} value={choice.value}>
                                {choice.title}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            {
                orders == null &&
                <div className="mmc-red text-primary">Unable to load orders</div>
            }
            {
                orders != null &&
                <div>
                        <div className="mmc-tabular-data-container">
                            <table className="table table-striped table-hover">
                                <thead className="table-dark">
                                <tr>
                                    <th>Order Id</th>
                                    <th>Order Number</th>
                                    <th>DeliverTo Name</th>
                                    <th>DeliverTo City</th>
                                    <th># Items</th>
                                    <th># PoItems</th>
                                    <th>Created Date</th>
                                    <th>Process Date</th>
                                    <th>Operations</th>
                                </tr>
                                </thead>
                                <tbody>
                                {
                                    (orders).map(function (item) {
                                        return (
                                            <tr key={item.orderId}>
                                                <td>{item.orderId}</td>
                                                <td>{item.orderNumber}</td>
                                                <td>{item.deliverToName}</td>
                                                <td>{item.deliverToCity}</td>
                                                <td>{item.orderItems.length}</td>
                                                <td>{item.purchaseOrderItems.length}</td>
                                                <td>{item.createdDateTime}</td>
                                                <td>{item.processStartDateTime}</td>
                                                <td>
                                                    <button className="btn btn-sm btn-outline-primary"
                                                            onClick={() => openDetailsFor(item)}>
                                                        View details
                                                    </button>
                                                    &nbsp;
                                                    <button className="btn btn-sm btn-outline-secondary"
                                                            onClick={() => openMmcOrderFor(item)}>
                                                        Show order
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                }
                                </tbody>
                            </table>
                        </div>
                    <div>
                        <Modal open={mmcOrderOpen}
                               onClose={closeMmcOrderModal}
                               closeOnEsc={false}
                               closeOnOverlayClick={false}
                               showCloseIcon={false}
                               classNames={{
                                   overlay: 'customOverlay',
                                   modal: 'customOrderDetailsModal',
                               }}>
                            <div><h2>MMC Order Email Contents</h2></div>

                            <div className="row mmc-form-button-row">
                                <div className='modal-navigation-row justify-content-end'>
                                    <button className="btn btn-outline-secondary"
                                            type="button"
                                            onClick={closeMmcOrderModal}>Close
                                    </button>
                                </div>
                            </div>

                            <div className='row detail-label-header'>
                                <div className="detail-label-data">
                                    <TextWithNewlines text={mmcOrder}/>
                                </div>
                            </div>

                            <div className="row mmc-form-button-row">
                                <div className='modal-navigation-row justify-content-end'>
                                    <button className="btn btn-outline-secondary"
                                            type="button"
                                            onClick={closeMmcOrderModal}>Close
                                    </button>
                                </div>
                            </div>
                        </Modal>
                    </div>
                    <div>
                        <Modal open={detailsOpen}
                               onClose={closeDetailsModal}
                               closeOnEsc={false}
                               closeOnOverlayClick={false}
                               showCloseIcon={false}
                               classNames={{
                                   overlay: 'customOverlay',
                                   modal: 'customOrderDetailsModal',
                               }}>
                                <div><h2>Order Details for {detailItem?.orderId}</h2></div>
                                <div className='detail-group border border-warning'>
                                    <div className='row detail-label-header'>
                                        <div className="detail-label-data">
                                        </div>
                                    </div>
                                    <div className='row detail-label-header'>
                                        <div className="detail-label-data">
                                            <div className="detail-label">Order number:</div>
                                            <div className="detail-value">{detailItem?.orderNumber}</div>
                                            <div className="detail-label">Created on:</div>
                                            <div className="detail-value">{detailItem?.createdDateTime}</div>
                                            <div className="detail-label">Processed on:</div>
                                            <div className="detail-value">{detailItem?.processStartDateTime}</div>
                                        </div>
                                    </div>
                                    <div className='row detail-label-header'>
                                        <div className="detail-label-data">
                                            <div className="detail-label">Shopify Id:</div>
                                            <div className="detail-value">{detailItem?.orderId}</div>
                                            <div className="detail-label">Deliver To:</div>
                                            <div className="detail-value">{detailItem?.deliverToName}</div>
                                        </div>
                                    </div>
                                    <div className='row detail-label-header'>
                                        <div className="detail-label-data">
                                            <div className="detail-label">Address:</div>
                                            <div className="detail-value">{detailItem?.deliverToAddress1}</div>
                                            <div className="detail-label">Address 2:</div>
                                            <div className="detail-value">{detailItem?.deliverToAddress2}</div>
                                        </div>
                                    </div>
                                    <div className='row detail-label-header'>
                                        <div className="detail-label-data">
                                            <div className="detail-label">City:</div>
                                            <div className="detail-value">{detailItem?.deliverToCity}</div>
                                            <div className="detail-label">State/Prov:</div>
                                            <div className="detail-value">{detailItem?.deliverToStateProv}</div>
                                            <div className="detail-label">Zip/PC:</div>
                                            <div className="detail-value">{detailItem?.deliverToZipPC}</div>
                                            <div className="detail-label">Country:</div>
                                            <div className="detail-value">{detailItem?.deliverToCountry}</div>
                                        </div>
                                    </div>
                                    <div className='row detail-label-header'>
                                        <div className="detail-label-data">
                                            <div className="detail-label">Order&nbsp;Notes:</div>
                                            <div className="detail-value">{detailItem?.orderNotes}</div>
                                        </div>
                                    </div>
                                </div>

                                {
                                    detailItem?.orderItems != null && detailItem?.orderItems.length > 0 &&
                                    <div className='detail-group detail-table border border-primary'>
                                        <div><h4>Order Line Items</h4></div>
                                        <table className="table table-striped table-hover">
                                            <thead className="table-dark">
                                            <tr>
                                                <th>Line Item Id</th>
                                                <th>Quantity</th>
                                                <th>MMC Sku</th>
                                                <th>Name</th>
                                                <th>Position</th>
                                                <th>Shopify Name</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {
                                                (detailItem.orderItems).map(function (orderItem, index) {
                                                    return (
                                                        <tr key={`${orderItem.lineItemId}-${index}`}>
                                                            <td>{orderItem.lineItemId}</td>
                                                            <td>{orderItem.quantity}</td>
                                                            <td>{orderItem.sku}</td>
                                                            <td>{orderItem.name}</td>
                                                            <td>{orderItem.position}</td>
                                                            <td>{orderItem.shopifyName}</td>
                                                        </tr>
                                                    );
                                                })
                                            }
                                            </tbody>
                                        </table>
                                    </div>
                                }

                                {
                                    detailItem?.purchaseOrderItems != null && detailItem?.purchaseOrderItems.length > 0 &&
                                    <div className='detail-group detail-table border border-secondary'>
                                        <div><h4>Purchase Order Items</h4></div>
                                        <table className="table table-striped table-hover">
                                            <thead className="table-dark">
                                            <tr>
                                                <th>PO #</th>
                                                <th>Distributor</th>
                                                <th>Quantity</th>
                                                <th>Their Sku</th>
                                                <th>Name</th>
                                                <th>Brand</th>
                                                <th>Color</th>
                                                <th>Style</th>
                                                <th>Size</th>
                                                <th>Submitted</th>
                                                <th>Status</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {
                                                (detailItem.purchaseOrderItems).map(function (poItem, index) {
                                                    return (
                                                        <tr key={`${poItem.poNumber}-${index}`}>
                                                            <td>{poItem.poNumber}</td>
                                                            <td>{poItem.distributor}</td>
                                                            <td>{poItem.quantity}</td>
                                                            <td>{poItem.sku}</td>
                                                            <td>{poItem.name}</td>
                                                            <td>{poItem.brand}</td>
                                                            <td>{poItem.color}</td>
                                                            <td>{poItem.style}</td>
                                                            <td>{poItem.size}</td>
                                                            <td>{poItem.submittedDateTime}</td>
                                                            <td>{poItem.successDateTime == null ? (poItem.failureDateTime == null ? "" : "Failed") : "Success"}</td>
                                                        </tr>
                                                    );
                                                })
                                            }
                                            </tbody>
                                        </table>
                                    </div>
                                }

                                <div className="row mmc-form-button-row">
                                    <div className='modal-navigation-row justify-content-end'>
                                        <button className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={closeDetailsModal}>Close
                                        </button>
                                    </div>
                                </div>
                            </Modal>
                        </div>
                    </div>
                }
            </div>
            );
            }
